/**
 * Extracts question text from various response formats
 * Handles: strings, objects with 'question' field, JSON strings
 */
export function extractQuestionText(question: any): string {
	if (typeof question === "string") {
		let raw = question.trim();

		// Handle markdown code blocks that contain JSON, e.g. ```json { ... } ```
		const codeBlockMatch = raw.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);

		if (codeBlockMatch) {
			const jsonPart = codeBlockMatch[1].trim();
			const textPart = raw
				.replace(/```(?:json)?\s*\n?[\s\S]*?\n?```/, "")
				.trim();

			// If there is meaningful text outside the code block, prefer that as the question
			if (textPart && textPart.length >= 5) {
				return textPart;
			}

			// Otherwise, try to parse the JSON block to extract a "question" field
			try {
				const parsed = JSON.parse(jsonPart);
				if (
					parsed &&
					typeof parsed === "object" &&
					parsed.question &&
					typeof parsed.question === "string"
				) {
					return parsed.question;
				}
			} catch {
				// Fall through to return raw below
			}
		}

		return raw;
	}

	if (question && typeof question === "object" && question.question) {
		return question.question;
	}

	if (question && typeof question === "object") {
		const str = JSON.stringify(question);
		const match = str.match(/"question":\s*"([^"]+)"/);
		if (match && match[1]) {
			return match[1];
		}
	}

	return String(question || "");
}

/**
 * Parses answer text to handle different input formats
 * Trims and validates non-empty answers
 */
export function parseAnswerText(answer: string): string | null {
	const trimmed = answer.trim();
	return trimmed.length > 0 ? trimmed : null;
}

/**
 * Gets a descriptive message for loading state
 */
export function getLoadingMessage(): string {
	return "Thinking...";
}

/**
 * Validates that required QA data is present
 */
export function validateQAData(
	currentQuestion: string,
	currentAnswer: string,
): { valid: boolean; error?: string } {
	if (!currentQuestion || !currentQuestion.trim()) {
		return { valid: false, error: "No question available" };
	}

	if (!currentAnswer || !currentAnswer.trim()) {
		return { valid: false, error: "Answer cannot be empty" };
	}

	return { valid: true };
}
